import type { BanknoteInstance } from '../models/BanknoteTypes';
import type { BanknoteCatalogService } from './BanknoteCatalogService';
import { BanknoteInstanceFactory } from './BanknoteInstanceFactory';
import { FakeVariantApplicator } from './FakeVariantApplicator';

function shuffle<T>(items: T[]): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j] as T, copy[i] as T];
  }
  return copy;
}

function instanceSignature(instance: BanknoteInstance): string {
  return `${instance.visual.hue}|${instance.visual.patternId}|${instance.serial}`;
}

export interface RoundBanknoteSet {
  readonly target: BanknoteInstance;
  readonly grid: readonly BanknoteInstance[];
}

/**
 * Tur banknot setini üretir — gerçek ve sahte aynı pipeline üzerinden.
 */
export class RoundBanknoteGenerator {
  private readonly instanceFactory = new BanknoteInstanceFactory();
  private readonly variantApplicator = new FakeVariantApplicator();

  constructor(private readonly catalogService: BanknoteCatalogService) {}

  generateRound(gridSize: number): RoundBanknoteSet {
    const design = this.catalogService.pickRandomDesign();
    const target = this.instanceFactory.createAuthentic(design);
    const fakes: BanknoteInstance[] = [];
    const signatures = new Set<string>([instanceSignature(target)]);

    let attempt = 0;
    while (fakes.length < gridSize - 1) {
      const variant = design.fakeVariants[fakes.length % design.fakeVariants.length];
      if (!variant) {
        break;
      }

      const applied = this.variantApplicator.apply(target, variant);
      const fake = this.instanceFactory.createFake(
        target,
        design,
        variant,
        applied.visual,
        applied.serial,
      );

      const signature = instanceSignature(fake);
      if (signatures.has(signature)) {
        attempt += 1;
        if (attempt > gridSize * 4) {
          break;
        }
        continue;
      }

      signatures.add(signature);
      fakes.push(fake);
      attempt = 0;
    }

    while (fakes.length < gridSize - 1) {
      const fallbackVariant = design.fakeVariants[fakes.length % design.fakeVariants.length];
      if (!fallbackVariant) {
        break;
      }

      const applied = this.variantApplicator.apply(target, fallbackVariant);
      const fallbackVisual = {
        ...applied.visual,
        hue: (applied.visual.hue + fakes.length * 3) % 360,
      };
      const fake = this.instanceFactory.createFake(
        target,
        design,
        fallbackVariant,
        fallbackVisual,
        `${applied.serial}-${fakes.length}`,
      );
      fakes.push(fake);
    }

    return {
      target,
      grid: shuffle([target, ...fakes]),
    };
  }
}
